/**
 * Bundled fallback copy for craft-help (Hints) popups.
 * Admin-published rows from GET /messages/hints override these when present.
 */

/** @typedef {{ title: string, content: string, actionLabel?: string }} HintText */

/** @type {Record<string, HintText>} */
export const HINT_COPY_FALLBACK = {
  B1: {
    title: "What's a Framework?",
    content:
      'A ready-made plan for a story, split into sequences. Brainstormy uses it to suggest what could happen in each one. You can skip it and pick one later. For example: a mystery starts with the crime and ends with who did it.',
  },
  B2: {
    title: 'Does this fit my story?',
    content:
      "Read the first few sequence names. If they sound like your story, it fits. It's only a starting point, and you can switch later. Not sure? Ask in the Outline chat which one suits your story.",
  },
  B3: {
    title: "What's on the Plot Grid?",
    content:
      "A sequence lands on the Plot Grid once you've picked its scenes. Those are the scenes you'll write. Next, open the sequence named here and chat about what happens in it.",
  },
  B4: {
    title: "What's a storyline?",
    content:
      "A thread you follow through the book, like a friendship or a secret. A subplot is a smaller thread. A setup and payoff is something shown early that matters later: a gun in the drawer in chapter 1 gets used in chapter 20. They keep threads from getting lost. You don't need any to start.",
  },
  B5: {
    title: 'Who tells the story?',
    content:
      "Whose eyes the reader sees through, and whether it says 'I' or 'she'. It changes how close the reader feels. It's optional; Brainstormy uses a sensible choice until you pick. For example: 'I opened the attic door.' or 'Theo opened the attic door.'",
  },
  B6: {
    title: "What's this?",
    content:
      "Notes about this kind of story, to give you ideas. It isn't part of your book and nothing here is required. Open it if you're stuck.",
  },
  S1: {
    title: 'What do these buttons do?',
    content:
      "Mute: you keep writing; Brainstormy stays quiet and takes notes. Teach: Brainstormy explains a writing idea. Get me started: Brainstormy asks easy questions to begin. Nudge: a few ideas to react to, once you've started.",
  },
  S4: {
    title: 'Is this permanent?',
    content:
      'No. You can undo it right after, or switch Framework later. Your scenes stay.',
  },
  S5: {
    title: 'Guide and Have I covered this?',
    content:
      "Guide: Brainstormy suggests what this sequence needs next. Have I covered this? Brainstormy checks whether you've said enough about this sequence to plan its scenes.",
  },
  S8: {
    title: 'Generate, Draft more, and Approve',
    content:
      'Generate suggests a first list of scenes. Draft more with AI adds a few more. Approve puts the scenes you keep on the Plot Grid. Nothing goes on the Plot Grid until you approve.',
  },
  S9: {
    title: "What's a Style?",
    content:
      'A saved Style is how your scenes sound: cozy, gritty, quick or quiet. It shapes the wording of the draft. Without one, Brainstormy writes plainly from your scene plan. Pick one on the Styles page.',
  },
  S10: {
    title: 'What was checked?',
    content:
      "Brainstormy compared this draft with your Book Notes and earlier scenes, looking for things that don't match, like a wrong name or eye color. Accepting saves the draft and you can still edit it. Then Brainstormy offers updates to your notes, and you choose.",
  },
  S11_THEME: {
    title: 'Theme',
    content:
      'What your story is really about underneath, like growing up or trust.',
  },
  S11_LOGLINE: {
    title: 'Logline',
    content:
      'Your story in one sentence. For example: A shy boy finds a dog that can speak and has to hide it from his town.',
  },
  S12: {
    title: 'Compendium',
    content:
      "Where things you save from chats go: To Dos, saved ideas and bookmarks. 'Showing' picks this book only or all your books.",
  },
};

export const HINT_GOT_IT = 'Got it';

export const HINTS_LABEL = 'Hints';
export const HINTS_ARIA_ON = 'Hints on. Tap any ? to see what something means.';
export const HINTS_ARIA_OFF = 'Hints off. Tap to show the ? icons again.';
export const HINTS_OFF_NOTICE = 'Hints off. Tap Hints to bring them back.';

/** @param {string} key */
export function fallbackHintText(key) {
  return HINT_COPY_FALLBACK[key] || null;
}

/** @param {string} key */
export function hintAriaLabel(key) {
  const row = HINT_COPY_FALLBACK[key];
  if (!row?.title) return 'Help';
  return `Help: ${row.title}`;
}
